import { useState, useMemo } from "react";
import {
  Box, Select, MenuItem, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField
} from "@mui/material";

export default function SegmentPresetBar({
  presets,
  selectedPreset,
  onSelect,
  onSave,
  onDelete
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");

  const storedUser = useMemo(
    () => JSON.parse(localStorage.getItem("adm_panel_user") || "{}"),
    []
  );
  const isDark = !!storedUser.darkmode;

  const selectSx = {
    minWidth: 260,
    backgroundColor: isDark ? "#1e293b" : "#ffffff",
    color: isDark ? "#e5e7eb" : "#374151",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: isDark ? "#334155" : "#d1d5db",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: isDark ? "#475569" : "#9ca3af",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#3b82f6",
    },
    "& .MuiSelect-icon": {
      color: isDark ? "#e5e7eb" : "#374151",
    },
  };

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: isDark ? "#1e293b" : "#ffffff",
        color: isDark ? "#e5e7eb" : "#374151",
        border: isDark ? "1px solid #334155" : "1px solid #e5e7eb",
        boxShadow: isDark 
          ? "0 10px 40px rgba(0,0,0,0.5)" 
          : "0 10px 40px rgba(0,0,0,0.15)",
      },
    },
  };

  const buttonSx = {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "14px",
  };

  const dialogSx = {
    "& .MuiDialog-paper": {
      backgroundColor: isDark ? "#1e293b" : "#ffffff",
      color: isDark ? "#e5e7eb" : "#374151",
      border: isDark ? "1px solid #334155" : "none",
    },
    "& .MuiDialogTitle-root": {
      color: isDark ? "#e5e7eb" : "#374151",
      fontWeight: 600,
    },
    "& .MuiTextField-root": {
      "& .MuiOutlinedInput-root": {
        backgroundColor: isDark ? "#0f172a" : "#ffffff",
        color: isDark ? "#e5e7eb" : "#374151",
        "& fieldset": {
          borderColor: isDark ? "#334155" : "#d1d5db",
        },
        "&:hover fieldset": {
          borderColor: isDark ? "#475569" : "#9ca3af",
        },
        "&.Mui-focused fieldset": {
          borderColor: "#3b82f6",
        },
      },
      "& .MuiInputLabel-root": {
        color: isDark ? "#94a3b8" : "#6b7280",
        "&.Mui-focused": {
          color: "#3b82f6",
        },
      },
    },
  };

  return (
    <Box 
      display="flex" 
      gap={2} 
      flexWrap="wrap" 
      alignItems="center" 
      mb={2}
      sx={{
        padding: "12px 16px",
        borderRadius: 2,
        backgroundColor: isDark ? "#1e293b" : "#ffffff",
        border: isDark ? "1px solid #334155" : "1px solid #e5e7eb",
        boxShadow: isDark 
          ? "0 2px 8px rgba(0,0,0,0.3)" 
          : "0 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      <Select
        size="small"
        value={selectedPreset?.name || "none"}
        onChange={(e) => onSelect(e.target.value)}
        sx={selectSx}
        MenuProps={menuProps}
      >
        <MenuItem 
          value="none"
          sx={{
            backgroundColor: isDark ? "#1e293b" : "#ffffff",
            color: isDark ? "#e5e7eb" : "#374151",
            "&:hover": {
              backgroundColor: isDark ? "#334155" : "#f3f4f6",
            },
            "&.Mui-selected": {
              backgroundColor: isDark ? "#2563eb" : "#3b82f6",
              color: "#ffffff",
              "&:hover": {
                backgroundColor: isDark ? "#1d4ed8" : "#2563eb",
              },
            },
          }}
        >
          none
        </MenuItem>
        {presets.map(p => (
          <MenuItem 
            key={p.objectid} 
            value={p.name}
            sx={{
              backgroundColor: isDark ? "#1e293b" : "#ffffff",
              color: isDark ? "#e5e7eb" : "#374151",
              "&:hover": {
                backgroundColor: isDark ? "#334155" : "#f3f4f6",
              },
              "&.Mui-selected": {
                backgroundColor: isDark ? "#2563eb" : "#3b82f6",
                color: "#ffffff",
                "&:hover": {
                  backgroundColor: isDark ? "#1d4ed8" : "#2563eb",
                },
              },
            }}
          >
            {p.name}
          </MenuItem>
        ))}
      </Select>

      {selectedPreset ? (
        <>
          <Button 
            variant="contained" 
            onClick={() => onSave(selectedPreset.name)}
            sx={{
              ...buttonSx,
              backgroundColor: "#3b82f6",
              "&:hover": {
                backgroundColor: "#2563eb",
              },
            }}
          >
            Update Selection
          </Button>

          <Button 
            color="error" 
            variant="outlined" 
            onClick={() => onDelete(selectedPreset.objectid)}
            sx={{
              ...buttonSx,
              borderColor: "#ef4444",
              color: "#ef4444",
              "&:hover": {
                borderColor: "#dc2626",
                backgroundColor: isDark ? "rgba(239, 68, 68, 0.1)" : "rgba(239, 68, 68, 0.05)",
              },
            }}
          >
            Delete
          </Button>
        </>
      ) : (
        <>
          <Button 
            variant="contained" 
            onClick={() => setOpen(true)}
            sx={{
              ...buttonSx,
              backgroundColor: "#3b82f6",
              "&:hover": {
                backgroundColor: "#2563eb",
              },
            }}
          >
            Save Selection
          </Button>

          <Dialog 
            open={open} 
            onClose={() => setOpen(false)}
            sx={dialogSx}
          >
            <DialogTitle>Save selection</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                fullWidth
                label="Preset name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                margin="normal"
              />
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => setOpen(false)}
                sx={{
                  color: isDark ? "#94a3b8" : "#6b7280",
                  "&:hover": {
                    backgroundColor: isDark ? "#334155" : "#f3f4f6",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  onSave(name);
                  setName("");
                  setOpen(false);
                }}
                sx={{
                  backgroundColor: "#3b82f6",
                  "&:hover": {
                    backgroundColor: "#2563eb",
                  },
                }}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}
